import type { StatKey, StatsConfig } from "../types";
import { useCountUp } from "../useCountUp";
import { formatNumber } from "./format";
import { StatIcon } from "./icons";

const SIZES = {
  sm: { num: "text-[24px] sm:text-[28px]", label: "text-[10.5px]", icon: "h-8 w-8", pad: "px-3 py-3" },
  md: { num: "text-[34px] sm:text-[42px]", label: "text-[11px] sm:text-[12px]", icon: "h-10 w-10", pad: "px-4 sm:px-5 py-4 sm:py-5" },
  lg: { num: "text-[44px] sm:text-[56px]", label: "text-[12px] sm:text-[13px]", icon: "h-12 w-12", pad: "px-5 sm:px-6 py-5 sm:py-7" },
} as const;

const ICON_SIZE = { sm: 14, md: 18, lg: 22 } as const;

interface Props {
  k: StatKey;
  value: number;
  label: string;
  cfg: StatsConfig;
  delayMs?: number;
}

/**
 * The one true "stat block" — used by every view (cards/inline/hero/stacked).
 * All visual variation comes from `cfg.size` + `cfg.density`.
 */
export function StatItem({ k, value, label, cfg, delayMs = 0 }: Props) {
  const sizing = SIZES[cfg.size];
  const animated = useCountUp(value, cfg.animate ? 1100 : 0);
  const numText = formatNumber(animated, cfg.format, cfg.locale);
  const iconPx = ICON_SIZE[cfg.size];
  return (
    <div
      className="lm-stat group relative flex items-start justify-between gap-3 animate-fadeIn"
      style={{
        animationDelay: cfg.animate ? `${delayMs}ms` : "0ms",
        animationFillMode: "both",
      }}
    >
      <div className="min-w-0">
        <div
          className={
            "tabular-nums font-semibold tracking-tight leading-[1.05] " + sizing.num
          }
          style={{ color: "var(--lm-fg)" }}
        >
          {numText}
        </div>
        <div
          className={
            "mt-1 font-semibold uppercase tracking-[0.08em] " + sizing.label
          }
          style={{ color: "var(--lm-label)" }}
        >
          {label}
        </div>
      </div>
      <div
        className={"shrink-0 grid place-items-center rounded-xl text-white shadow-soft " + sizing.icon}
        style={{
          background: `linear-gradient(135deg, var(--lm-accent) 0%, ${shade(cfg.accent, -0.22)} 100%)`,
        }}
        aria-hidden
      >
        <StatIcon k={k} className={`w-[${iconPx}px] h-[${iconPx}px]`} />
      </div>
    </div>
  );
}

export function shade(hex: string, amount: number): string {
  const c = hex.replace("#", "");
  const num = parseInt(c, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.min(255, r + Math.round(255 * amount)));
  g = Math.max(0, Math.min(255, g + Math.round(255 * amount)));
  b = Math.max(0, Math.min(255, b + Math.round(255 * amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export function getSizing(size: "sm" | "md" | "lg") {
  return SIZES[size];
}
